/** Defaults tab §A — the operator's default tag categories (backend plan 2026-10-07 §A2).
 *
 *  The categories every tour that uses the operator defaults gets on top of its own (a
 *  tour's own row for the same category wins). Shown in the shared
 *  `TagCategoryAssociationsTable`: view mode by default; Edit (with `console_top_manage`)
 *  copies the stored rows into a local `associationsReducer` draft, so a refetch never
 *  touches what is being edited and the PUT carries the version the draft started from.
 *
 *  - A licensee of a franchise parent sees the parent's defaults as inherited rows ("from
 *    ‹parent›") and may override one; picking an inherited category in "Add a category"
 *    overrides it too.
 *  - Save is blocked while a row left in has no category (its reference no longer
 *    resolves — the server requires one; removing the row unblocks it) or the edit makes a
 *    check-in / boarding limit worse than stored (the server's no-worse rule); otherwise a
 *    dialog lists the changes in words and asks for a reason, then PUTs the whole list
 *    (`toWireList`: display order, removed rows left out).
 *  - 409 `defaults_modified`: Reload (confirmed — it drops the draft), no merge. 400 row
 *    errors land under their rows: the PUT's `rows["<i>"]` indexes the rows that were SENT,
 *    i.e. the non-removed rows in display order. They describe the draft as sent: a row
 *    edited since loses its line, and any edit clears the status. */
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { Alert, Box, Button, Link, Skeleton, Typography } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useReducer, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import type { TourOperatorDetail } from '@/api/tourOperators';
import {
  defaultTagCategoriesKey,
  defaultsRowErrors,
  isDefaultsModified,
  putDefaultTagCategories,
  type OperatorDefaultTagCategories,
  type OperatorDefaultTagCategoryWriteRow,
} from '@/api/tourOperatorDefaults';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { FieldHelp } from '@/components/FieldHelp';
import { useSnackbar } from '@/components/Snackbar';
import { act, type ValueAction } from '@/components/ValueActions/ValueActions';
import {
  associationsReducer,
  baselineOf,
  describeDiff,
  diffAssociations,
  limitState,
  rowName,
} from '@/features/tag-categories/associations/associationsReducer';
import { FLAG_BY_KEY, isFlag } from '@/features/tag-categories/associations/flags';
import { TagCategoryAssociationsTable } from '@/features/tag-categories/associations/TagCategoryAssociationsTable';
import type {
  AssocAction,
  AssocCategory,
  AssocRow,
  InheritedRow,
} from '@/features/tag-categories/associations/types';
import {
  fromWire,
  inheritedFromWire,
  rowsWithoutCategory,
  toWireList,
} from '@/features/tag-categories/associations/wire';
import { tagCategoryActions } from '@/features/tags/tagLinks';

import { operatorDefaultsHref } from '../tenantLinks';
import { AddCategoryPicker } from './AddCategoryPicker';
import { DefaultsSection } from './DefaultsSection';
import { DEFAULTS_HELP } from './fieldHelp';
import { apiErrorText, useDefaultTagCategories } from './queries';
import { SaveDefaultTagCategoriesDialog } from './SaveDefaultTagCategoriesDialog';
import { sourceWords } from './tripSettings';

type Usage = OperatorDefaultTagCategories['usage'];
type Resolved = OperatorDefaultTagCategories['tours_may_edit'];

interface Seed {
  rows: AssocRow[];
  inherited: InheritedRow[];
  baseline: ReadonlyMap<string, AssocRow>;
  version: string;
}

function seedOf(data: OperatorDefaultTagCategories): Seed {
  const rows = data.rows.map((r) => fromWire(r));
  return {
    rows,
    inherited: data.inherited.map((r) => inheritedFromWire(r, r.owner)),
    baseline: baselineOf(rows),
    version: data.version,
  };
}

type DraftAction = AssocAction | { type: 'reset'; rows: readonly AssocRow[] };
const draftReducer = (rows: readonly AssocRow[], a: DraftAction): readonly AssocRow[] =>
  a.type === 'reset' ? a.rows : associationsReducer(rows, a);

const NO_ROWS: readonly AssocRow[] = [];
const NO_INHERITED: readonly InheritedRow[] = [];

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

/** A row error's field in words ('' — the message alone). Covers the keys the PUT's row
 *  errors use — the row serializer, the association rules and the writer (`row`, `id`,
 *  `category_id`, `sequence`, the counts, the flags); any other key shows its message
 *  alone, never a raw field name. */
function fieldWords(field: string): string {
  if (isFlag(field)) return FLAG_BY_KEY[field].label;
  if (field === 'min_tags' || field === 'max_tags') return 'Tags per guest';
  if (field === 'category_id') return 'Category';
  if (field === 'sequence') return 'Order';
  return '';
}

/** Why rows without a category block the save, naming them. */
function noCategoryWords(rows: readonly AssocRow[]): string {
  const names = rows.map(rowName).join(', ');
  return rows.length === 1
    ? `Row ${names} has no category — remove it to save`
    : `Rows ${names} have no category — remove them to save`;
}

/** Who a saved change reaches, in words. */
export function reachText(usage: Usage): string {
  const base = usage.operator_wide.value
    ? `they reach all ${plural(usage.own_tours, 'tour')} of this operator`
    : `they reach the ${plural(usage.own_tours, 'tour')} that opt in`;
  return usage.licensee_tours != null
    ? `${base}, plus ${plural(usage.licensee_tours, 'licensee tour')}.`
    : `${base}.`;
}

interface Props {
  to: TourOperatorDetail;
  canManage: boolean;
  /** This section is the one being edited (one at a time — the tab decides). */
  editing: boolean;
  /** Why Edit is unavailable (the other section is being edited). */
  editBlockedReason: string | null;
  onEditingChange: (editing: boolean) => void;
  onDirtyChange: (dirty: boolean) => void;
}

export function DefaultTagCategoriesSection({
  to,
  canManage,
  editing,
  editBlockedReason,
  onEditingChange,
  onDirtyChange,
}: Props) {
  const query = useDefaultTagCategories(to.id);
  const qc = useQueryClient();
  const snackbar = useSnackbar();

  const view = useMemo(() => (query.data ? seedOf(query.data) : null), [query.data]);
  // The draft's start: the rows it diffs against and the version its PUT carries.
  const [draft, setDraft] = useState<Seed | null>(null);
  const [draftRows, dispatch] = useReducer(draftReducer, NO_ROWS);
  const isEditing = editing && draft !== null;

  const rows = isEditing ? draftRows : (view?.rows ?? NO_ROWS);
  const inherited = (isEditing ? draft.inherited : view?.inherited) ?? NO_INHERITED;
  const baseline = isEditing ? draft.baseline : view?.baseline;

  const diff = useMemo(
    () => (isEditing ? diffAssociations(draft.baseline, draftRows) : null),
    [isEditing, draft, draftRows],
  );
  const count = diff?.count ?? 0;
  const dirty = count > 0;
  useEffect(() => {
    onDirtyChange(dirty);
  }, [dirty, onDirtyChange]);

  const limits = useMemo(() => limitState(rows, inherited, baseline), [rows, inherited, baseline]);
  // `toWireList` cannot write a row without a category (it throws): Save waits for its removal.
  const uncategorized = useMemo(() => rowsWithoutCategory(rows), [rows]);
  const blockReason = uncategorized.length
    ? noCategoryWords(uncategorized)
    : limits.worse
      ? `${limits.problems.join(' · ')} — clear one to save`
      : null;

  // ---- save ---------------------------------------------------------------
  const [saveOpen, setSaveOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [conflict, setConflict] = useState(false);
  const [dialogError, setDialogError] = useState<string | null>(null);
  /** The last 400: its words (the status line), the messages by row key, and the draft it
   *  answered — the rows as sent, so an edit since makes them stale. */
  const [rowErrors, setRowErrors] = useState<{
    detail: string;
    byKey: ReadonlyMap<string, Record<string, string>>;
    draft: readonly AssocRow[];
  } | null>(null);
  const [reloadOpen, setReloadOpen] = useState(false);

  const clearErrors = () => {
    setConflict(false);
    setDialogError(null);
    setRowErrors(null);
  };

  const startEdit = (data: OperatorDefaultTagCategories) => {
    const seed = seedOf(data);
    dispatch({ type: 'reset', rows: seed.rows });
    setDraft(seed);
    clearErrors();
    onEditingChange(true);
  };
  const leaveEdit = () => {
    setDraft(null);
    dispatch({ type: 'reset', rows: NO_ROWS });
    clearErrors();
    setReason('');
    setSaveOpen(false);
    onEditingChange(false);
  };

  const mutation = useMutation({
    mutationFn: (v: {
      body: { rows: OperatorDefaultTagCategoryWriteRow[]; version: string; reason: string };
      draft: readonly AssocRow[];
    }) => putDefaultTagCategories(to.id, v.body),
    onSuccess: (data) => {
      qc.setQueryData(defaultTagCategoriesKey(to.id), data);
      void qc.invalidateQueries({ queryKey: defaultTagCategoriesKey(to.id) });
      void qc.invalidateQueries({ queryKey: ['tourOperators', 'detail', to.id] });
      // Tour pages show the defaults as inherited rows.
      void qc.invalidateQueries({ queryKey: ['tours', 'detail'] });
      snackbar.show('Default tag categories saved.', 'success');
      leaveEdit();
    },
    onError: (e, v) => {
      if (isDefaultsModified(e)) {
        setConflict(true);
        return;
      }
      const bad = defaultsRowErrors(e);
      if (bad) {
        // What toWireList sent, in the same order — the 400's row indexes point into it.
        const sent = v.draft.filter((r) => r.state !== 'removed');
        const byKey = new Map<string, Record<string, string>>();
        for (const [index, fields] of Object.entries(bad.rows)) {
          const row = sent[Number(index)];
          if (row) byKey.set(row.key, fields);
        }
        setRowErrors({
          detail: bad.detail || 'Some rows can’t be saved as they are.',
          byKey,
          draft: v.draft,
        });
        // The rows say what is wrong; the dialog would cover them.
        setSaveOpen(false);
        return;
      }
      setDialogError(apiErrorText(e));
    },
  });

  const openSave = useCallback(() => {
    setDialogError(null);
    setSaveOpen(true);
  }, []);

  const confirmSave = (text: string) => {
    if (!draft) return;
    setDialogError(null);
    setRowErrors(null);
    mutation.mutate({
      // Save is blocked while a row has no category (`blockReason`), so this never throws.
      body: {
        rows: toWireList(draftRows) as OperatorDefaultTagCategoryWriteRow[],
        version: draft.version,
        reason: text,
      },
      draft: draftRows,
    });
  };

  const reload = async () => {
    setReloadOpen(false);
    setSaveOpen(false);
    const fresh = await query.refetch();
    if (fresh.data) {
      startEdit(fresh.data);
      snackbar.show('Reloaded the saved defaults — your changes were dropped.', 'info');
    } else {
      snackbar.show('Couldn’t reload the default tag categories.', 'error');
    }
  };

  // ---- table seams ----------------------------------------------------------
  /** Each category's owner (this operator or the franchise parent) scopes its links. */
  const ownerOf = useMemo(() => {
    const m = new Map<number, number>();
    for (const r of [...(query.data?.rows ?? []), ...(query.data?.inherited ?? [])]) {
      // A category reference that no longer resolves reads as null (`fromWire`).
      if (r.category?.tour_operator_id != null) m.set(r.category.id, r.category.tour_operator_id);
    }
    return m;
  }, [query.data]);
  const categoryActions = useCallback(
    (c: AssocCategory): ValueAction[] =>
      c.code
        ? tagCategoryActions({ id: c.id, code: c.code }, { tenantId: ownerOf.get(c.id) ?? to.id })
        : [act.open(`/tag-categories/${c.id}`, 'Open category')],
    [ownerOf, to.id],
  );
  const renderRowAddon = useCallback(
    (row: AssocRow) => {
      const fields = rowErrors?.byKey.get(row.key);
      // The reducer keeps an untouched row's object: another one is the row edited since.
      if (!fields || !rowErrors?.draft.includes(row)) return null;
      const text = Object.entries(fields)
        .map(([f, msg]) => (fieldWords(f) ? `${fieldWords(f)}: ${msg}` : msg))
        .join(' · ');
      return (
        <Typography
          variant="caption"
          color="error"
          sx={{ display: 'flex', gap: 0.5, alignItems: 'flex-start' }}
          data-row-error={row.key}
        >
          <ErrorOutlineIcon aria-hidden sx={{ fontSize: 16 }} />
          <span>Not saved — {text}</span>
        </Typography>
      );
    },
    [rowErrors],
  );

  // The save's words hold for the draft it answered; any edit makes them stale.
  const status = rowErrors && rowErrors.draft === draftRows ? rowErrors.detail : null;

  return (
    <DefaultsSection
      id="tag-categories"
      topId={to.id}
      title="Default tag categories"
      description="The categories every tour that uses the operator defaults gets, on top of its own — a tour’s own row for the same category wins."
      edit={{
        canManage,
        editing: isEditing,
        editBlockedReason:
          editBlockedReason ?? (query.data ? null : 'The default tag categories have not loaded'),
        count,
        saving: mutation.isPending,
        blockReason,
        dialogOpen: saveOpen || reloadOpen,
        error: status,
        onEdit: () => query.data && startEdit(query.data),
        onDiscard: leaveEdit,
        onSave: openSave,
      }}
    >
      {query.isError ? (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => void query.refetch()}>
              Retry
            </Button>
          }
        >
          Couldn’t load the default tag categories. {apiErrorText(query.error, '')}
        </Alert>
      ) : !query.data ? (
        <Box sx={{ display: 'grid', gap: 1 }} data-defaults-loading>
          <Skeleton variant="text" width={420} />
          <Skeleton variant="rounded" height={240} />
        </Box>
      ) : (
        <>
          <UsageFacts
            topId={to.id}
            parentName={to.parent_name}
            usage={query.data.usage}
            toursMayEdit={query.data.tours_may_edit}
          />
          <TagCategoryAssociationsTable
            label="Default tag categories"
            emptyText="No default tag categories yet."
            rows={rows}
            inherited={inherited}
            baseline={baseline}
            editable={isEditing}
            onAction={dispatch}
            addPicker={
              isEditing ? (
                <AddCategoryPicker
                  topId={to.id}
                  tenantCode={to.code}
                  parentCode={to.parent_code}
                  rows={rows}
                  inherited={inherited}
                  onAdd={(category) => dispatch({ type: 'add', category })}
                  onOverride={(from) => dispatch({ type: 'override', from })}
                />
              ) : undefined
            }
            categoryActions={categoryActions}
            renderRowAddon={renderRowAddon}
          />
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'flex', gap: 0.5, alignItems: 'flex-start', maxWidth: '80ch' }}
            data-defaults-effect
          >
            <InfoOutlinedIcon aria-hidden sx={{ fontSize: 16 }} />
            <span>
              Check-in, self-registration, booking management and the dashboard read these defaults
              live. Chat channels and the guest app pick up a change on each tour’s next save.
            </span>
          </Typography>
        </>
      )}

      <SaveDefaultTagCategoriesDialog
        open={saveOpen}
        lines={diff ? describeDiff(diff) : []}
        count={count}
        reach={query.data ? reachText(query.data.usage) : ''}
        reason={reason}
        onReasonChange={setReason}
        saving={mutation.isPending}
        conflict={conflict}
        error={dialogError}
        onReload={() => setReloadOpen(true)}
        onConfirm={confirmSave}
        onCancel={() => setSaveOpen(false)}
      />
      <ConfirmDialog
        open={reloadOpen}
        title="Reload the default tag categories?"
        message={`Your ${plural(count, 'change')} will be dropped and the table shows the saved defaults again.`}
        confirmLabel="Drop my changes and reload"
        destructive
        onConfirm={() => void reload()}
        onCancel={() => setReloadOpen(false)}
      />
    </DefaultsSection>
  );
}

/** Who gets the defaults and whether tours may change them — each setting a link to the
 *  trip settings where it is edited. */
function UsageFacts({
  topId,
  parentName,
  usage,
  toursMayEdit,
}: {
  topId: number;
  parentName: string | null;
  usage: Usage;
  toursMayEdit: Resolved;
}) {
  const href = operatorDefaultsHref(topId, 'trip-settings');
  const source = (r: Resolved) => {
    const words = sourceWords(r.source, parentName);
    return words.charAt(0).toLowerCase() + words.slice(1);
  };
  return (
    <Box
      component="ul"
      sx={{
        m: 0,
        p: 0,
        listStyle: 'none',
        display: 'flex',
        flexWrap: 'wrap',
        columnGap: 3,
        rowGap: 0.5,
        typography: 'body2',
        '& li': { display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' },
      }}
      data-defaults-usage
    >
      <li data-usage="reach">
        <Link component={RouterLink} to={href}>
          {usage.operator_wide.value
            ? 'All tours of this operator use them'
            : 'Only tours that opt in use them'}
        </Link>
        <Box component="span" sx={{ color: 'text.secondary' }}>
          ({source(usage.operator_wide)}) ·
        </Box>
        <Box component="span" sx={{ fontVariantNumeric: 'tabular-nums' }}>
          {plural(usage.own_tours, 'tour')}
        </Box>
        <FieldHelp help={DEFAULTS_HELP.own_tours} />
      </li>
      {usage.licensee_tours != null && (
        <li data-usage="licensees">
          <Box component="span" sx={{ fontVariantNumeric: 'tabular-nums' }}>
            {plural(usage.licensee_tours, 'licensee tour')} get them too
          </Box>
          <FieldHelp help={DEFAULTS_HELP.licensee_tours} />
        </li>
      )}
      <li data-usage="tours-may-edit">
        <Link component={RouterLink} to={href}>
          {toursMayEdit.value
            ? 'Tours may change their own tag categories'
            : 'Tours can’t change their own tag categories'}
        </Link>
        <Box component="span" sx={{ color: 'text.secondary' }}>
          ({source(toursMayEdit)})
        </Box>
      </li>
    </Box>
  );
}
